import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Sidebar from './Sidebar';
import Swal from 'sweetalert2';
import { jsPDF } from 'jspdf';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFilePdf,
  faDownload,
  faUsers,
  faClock,
  faGraduationCap,
  faLayerGroup,
  faPrint,
  faSpinner,
  faCheckCircle,
  faTimes,
  faArrowLeft,
  faFileAlt,
  faUserGraduate,
  faCalendarAlt,
  faSchool,
  faCog,
  faBars,
  faChevronDown,
  faChevronUp,
  faFilter,
  faSearch,
  faEye,
  faList,
  faClipboardList,
  faChartLine,
  faPercent,
  faAward,
  faBookOpen,
  faHistory,
  faInfoCircle,
  faPlus,
  faMinus
} from "@fortawesome/free-solid-svg-icons";
import './dashboard.css';
import './SecondAss.css';

const API_BASE_URL = "https://mhsdev.online/MHS-WEB/api";

// Exam type configurations
const EXAM_TYPES_CONFIG = {
  '1st Assessment': {
    id: '1st-assessment',
    displayName: '1st Assessment',
    columns: [
      { key: 'subject', label: 'Subjects' },
      { key: 'total', label: 'Total Marks' },
      { key: 'obtained', label: 'Obtained' },
      { key: 'percentage', label: 'Percentage' },
      { key: 'grade', label: 'Grade' }
    ],
    previousAssessments: [],
    hasPreviousAssessment: false,
    tableType: 'simple',
    description: 'First term assessment - Simple marks sheet'
  },
  'Mid Term': {
    id: 'mid-term',
    displayName: 'Mid Term',
    columns: [
      { key: 'subject', label: 'Subjects' },
      { 
        key: 'previous',
        label: 'Previous Assessment',
        subColumns: [
          { key: 'previous_total', label: 'Total Marks' },
          { key: 'previous_obtained', label: 'Obtained' }
        ]
      },
      {
        key: 'current',
        label: 'Current Assessment',
        subColumns: [
          { key: 'total', label: 'Total Marks' },
          { key: 'obtained', label: 'Obtained' },
          { key: 'percentage', label: 'Percentage' },
          { key: 'grade', label: 'Grade' }
        ]
      }
    ],
    previousAssessments: ['1st Assessment'],
    hasPreviousAssessment: true,
    tableType: 'complex',
    description: 'Mid-term exam - Includes 1st Assessment marks'
  },
  '2nd Assessment': {
    id: '2nd-assessment',
    displayName: '2nd Assessment',
    columns: [
      { key: 'subject', label: 'Subjects' },
      { 
        key: 'previous',
        label: 'Previous Assessment',
        subColumns: [
          { key: 'previous_total', label: 'Total Marks (1st+Mid)' },
          { key: 'previous_obtained', label: 'Obtained (1st+Mid)' }
        ]
      },
      {
        key: 'current',
        label: 'Current Assessment',
        subColumns: [
          { key: 'total', label: 'Total Marks' },
          { key: 'obtained', label: 'Obtained' },
          { key: 'percentage', label: 'Percentage' },
          { key: 'grade', label: 'Grade' }
        ]
      }
    ],
    previousAssessments: ['1st Assessment', 'Mid Term'],
    hasPreviousAssessment: true,
    tableType: 'complex',
    description: 'Second term assessment - Includes 1st + Mid Term marks'
  },
  'Final': {
    id: 'final',
    displayName: 'Final',
    columns: [
      { key: 'subject', label: 'Subjects' },
      { 
        key: 'previous',
        label: 'Previous Assessment',
        subColumns: [
          { key: 'previous_total', label: 'Total Marks (1st+Mid+2nd)' },
          { key: 'previous_obtained', label: 'Obtained (1st+Mid+2nd)' }
        ]
      },
      {
        key: 'current',
        label: 'Current Assessment',
        subColumns: [
          { key: 'total', label: 'Total Marks' },
          { key: 'obtained', label: 'Obtained' },
          { key: 'percentage', label: 'Percentage' },
          { key: 'grade', label: 'Grade' }
        ]
      }
    ],
    previousAssessments: ['1st Assessment', 'Mid Term', '2nd Assessment'],
    hasPreviousAssessment: true,
    tableType: 'complex',
    description: 'Final exam - Includes all previous assessments'
  }
};

const ResultCardGenerator = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // State for data
  const [students, setStudents] = useState([]);
  const [examMarks, setExamMarks] = useState({});
  const [previousMarks, setPreviousMarks] = useState({});
  const [loading, setLoading] = useState(false);
  const [pdfGenerating, setPdfGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [generatedPDFs, setGeneratedPDFs] = useState([]);
  
  // State for UI
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [showDetails, setShowDetails] = useState({});
  const [viewMode, setViewMode] = useState("grid");
  const [groupByClass, setGroupByClass] = useState(true);
  
  // Generation steps
  const [generationSteps, setGenerationSteps] = useState([]);
  const [currentStep, setCurrentStep] = useState('');
  const [showGenerationModal, setShowGenerationModal] = useState(false);
  
  // Get data from navigation state
  const { state } = location;
  const examType = state?.examType || '1st Assessment';
  const isBulk = state?.isBulk || false;
  const initialStudents = state?.students || [];
  const examConfig = EXAM_TYPES_CONFIG[examType] || EXAM_TYPES_CONFIG['1st Assessment'];

  useEffect(() => {
    if (initialStudents.length > 0) {
      setStudents(initialStudents);
      setSelectedStudents(initialStudents.map(s => s.student_id));
      fetchAllExamMarks(initialStudents);
    } else {
      Swal.fire({
        title: 'No Students Selected',
        text: 'Please select students to generate result cards',
        icon: 'warning',
        confirmButtonColor: '#f59e0b',
      }).then(() => {
        navigate('/result-management');
      });
    }
  }, [initialStudents, examType]);

  const fetchAllExamMarks = async (studentList) => {
    setLoading(true);
    try {
      const studentIds = studentList.map(s => s.student_id);
      
      // Fetch current exam marks
      const currentResponse = await fetch(`${API_BASE_URL}/ResultController.php?action=getExamMarks`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          studentIds: studentIds,
          examType: examType
        })
      });

      if (!currentResponse.ok) {
        throw new Error(`HTTP error! status: ${currentResponse.status}`);
      }

      const currentResult = await currentResponse.json();
      
      if (currentResult.success) {
        setExamMarks(currentResult.marks);
        
        // If exam type has previous assessments, fetch them too
        if (examConfig.previousAssessments && examConfig.previousAssessments.length > 0) {
          const previousMarksData = {};
          
          // Fetch each previous assessment
          for (const prevExamType of examConfig.previousAssessments) {
            const prevResponse = await fetch(`${API_BASE_URL}/ResultController.php?action=getExamMarks`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                studentIds: studentIds,
                examType: prevExamType
              })
            });

            if (prevResponse.ok) {
              const prevResult = await prevResponse.json();
              if (prevResult.success) {
                previousMarksData[prevExamType] = prevResult.marks;
              }
            }
          }
          
          setPreviousMarks(previousMarksData);
        }
      } else {
        Swal.fire({
          title: 'Error!',
          text: 'Failed to fetch exam marks data',
          icon: 'error',
          confirmButtonColor: '#d33',
        });
      }
    } catch (error) {
      console.error('Error fetching exam marks:', error);
      Swal.fire({
        title: 'Error!',
        text: 'Failed to load exam marks. Please try again.',
        icon: 'error',
        confirmButtonColor: '#d33',
      });
    } finally {
      setLoading(false);
    }
  };

  // Calculate aggregated previous marks for a student
  const calculatePreviousMarks = (studentId) => {
    if (!examConfig.previousAssessments || examConfig.previousAssessments.length === 0) {
      return { total: 0, obtained: 0, subjects: {} };
    }

    let totalPrevious = 0;
    let obtainedPrevious = 0;
    const subjectTotals = {};

    // Aggregate marks from all previous assessments
    examConfig.previousAssessments.forEach(prevExam => {
      const prevMarks = previousMarks[prevExam]?.[studentId] || [];
      
      prevMarks.forEach(mark => {
        const subject = mark.subject || 'Unknown';
        const total = parseFloat(mark.total_marks) || 0;
        const obtained = parseFloat(mark.obtained_marks) || 0;
        
        if (!subjectTotals[subject]) {
          subjectTotals[subject] = { total: 0, obtained: 0 };
        }
        
        subjectTotals[subject].total += total;
        subjectTotals[subject].obtained += obtained;
        totalPrevious += total;
        obtainedPrevious += obtained;
      });
    });

    return {
      total: totalPrevious,
      obtained: obtainedPrevious,
      subjects: subjectTotals
    };
  };

  // Calculate student performance with previous assessment data
  const calculateStudentPerformance = (studentId) => {
    const marks = examMarks[studentId] || [];
    const previous = calculatePreviousMarks(studentId);
    
    if (marks.length === 0) {
      return {
        total: 0,
        obtained: 0,
        percentage: 0,
        grade: 'N/A',
        previousTotal: previous.total,
        previousObtained: previous.obtained,
        subjects: []
      };
    }

    let totalMarks = 0;
    let obtainedMarks = 0;
    let subjects = [];

    marks.forEach(mark => {
      const subjectName = mark.subject || 'Unknown';
      const total = parseFloat(mark.total_marks) || 0;
      const obtained = parseFloat(mark.obtained_marks) || 0;
      const prevSubject = previous.subjects[subjectName] || { total: 0, obtained: 0 };
      
      totalMarks += total;
      obtainedMarks += obtained;
      
      const percentage = total > 0 ? (obtained / total) * 100 : 0;
      const grade = calculateGrade(percentage);
      
      subjects.push({
        name: subjectName,
        total: total,
        obtained: obtained,
        percentage: percentage,
        grade: grade,
        previousTotal: prevSubject.total,
        previousObtained: prevSubject.obtained
      });
    });

    const overallPercentage = totalMarks > 0 ? (obtainedMarks / totalMarks) * 100 : 0;
    const overallGrade = calculateGrade(overallPercentage);

    return {
      total: totalMarks,
      obtained: obtainedMarks,
      percentage: overallPercentage,
      grade: overallGrade,
      previousTotal: previous.total,
      previousObtained: previous.obtained,
      subjects: subjects
    };
  };

  const calculateGrade = (percentage) => {
    if (percentage >= 90) return 'A++';
    if (percentage >= 80) return 'A+';
    if (percentage >= 70) return 'A';
    if (percentage >= 60) return 'B+';
    if (percentage >= 50) return 'B';
    if (percentage >= 40) return 'C';
    return 'F';
  };

  const getGradeColor = (grade) => {
    switch(grade) {
      case 'A++': return '#8B008B';
      case 'A+': return '#39B54A';
      case 'A': return '#0E4B95';
      case 'B+': return '#006837';
      case 'B': return '#FF9800';
      case 'C': return '#FF5722';
      default: return '#ED1C24';
    }
  };

  // Render table headers based on exam type
  const renderTableHeaders = () => {
    if (examConfig.tableType === 'simple') {
      return (
        <thead>
          <tr>
            {examConfig.columns.map((column, index) => (
              <th key={index}>{column.label}</th>
            ))}
          </tr>
        </thead>
      );
    } else {
      return (
        <thead>
          <tr>
            {examConfig.columns.map((column, index) => {
              if (column.key === 'subject') {
                return <th key={index} rowSpan="2">{column.label}</th>;
              } else if (column.subColumns) {
                return (
                  <th key={index} colSpan={column.subColumns.length}>
                    {column.label}
                  </th>
                );
              }
              return null;
            })}
          </tr>
          <tr>
            {examConfig.columns.map((column, index) => {
              if (column.subColumns) {
                return column.subColumns.map((subCol, subIndex) => (
                  <th key={`${index}-${subIndex}`}>{subCol.label}</th>
                ));
              } else if (column.key !== 'subject') {
                return <th key={index}>{column.label}</th>;
              }
              return null;
            })}
          </tr>
        </thead>
      );
    }
  };

  // Render subject rows based on exam type
  const renderSubjectRows = (subjects) => {
    return subjects.map((subject, index) => {
      if (examConfig.tableType === 'simple') {
        return (
          <tr key={index}>
            <td>{subject.name}</td>
            <td>{subject.total}</td>
            <td>{subject.obtained}</td>
            <td>{subject.percentage.toFixed(1)}%</td>
            <td>
              <span 
                className="SecondAss-subjectGrade"
                style={{ backgroundColor: getGradeColor(subject.grade) }}
              >
                {subject.grade}
              </span>
            </td>
          </tr>
        );
      } else {
        return (
          <tr key={index}>
            <td>{subject.name}</td>
            <td>{subject.previousTotal}</td>
            <td>{subject.previousObtained}</td>
            <td>{subject.total}</td>
            <td>{subject.obtained}</td>
            <td>{subject.percentage.toFixed(1)}%</td>
            <td>
              <span 
                className="SecondAss-subjectGrade"
                style={{ backgroundColor: getGradeColor(subject.grade) }}
              >
                {subject.grade}
              </span>
            </td>
          </tr>
        );
      }
    });
  };

  // Render performance summary based on exam type
  const renderPerformanceSummary = (performance) => {
    if (examConfig.tableType === 'simple') {
      return (
        <div className="SecondAss-performanceSummary">
          <div className="SecondAss-performanceItem">
            <span className="SecondAss-perfLabel">Total Marks:</span>
            <span className="SecondAss-perfValue">{performance.total}</span>
          </div>
          <div className="SecondAss-performanceItem">
            <span className="SecondAss-perfLabel">Obtained:</span>
            <span className="SecondAss-perfValue">{performance.obtained}</span>
          </div>
          <div className="SecondAss-performanceItem">
            <span className="SecondAss-perfLabel">Percentage:</span>
            <span className="SecondAss-perfValue SecondAss-percentage">
              {performance.percentage.toFixed(1)}%
            </span>
          </div>
          <div className="SecondAss-performanceItem">
            <span className="SecondAss-perfLabel">Grade:</span>
            <span 
              className="SecondAss-perfValue SecondAss-grade"
              style={{ backgroundColor: getGradeColor(performance.grade) }}
            >
              {performance.grade}
            </span>
          </div>
        </div>
      );
    } else {
      return (
        <div className="SecondAss-performanceSummary">
          <div className="SecondAss-performanceItem SecondAss-previousItem">
            <span className="SecondAss-perfLabel">
              <FontAwesomeIcon icon={faHistory} />
              Previous Total:
            </span>
            <span className="SecondAss-perfValue">{performance.previousTotal}</span>
          </div>
          <div className="SecondAss-performanceItem SecondAss-previousItem">
            <span className="SecondAss-perfLabel">
              <FontAwesomeIcon icon={faHistory} />
              Previous Obtained:
            </span>
            <span className="SecondAss-perfValue">{performance.previousObtained}</span>
          </div>
          <div className="SecondAss-performanceItem SecondAss-currentItem">
            <span className="SecondAss-perfLabel">Current Total:</span>
            <span className="SecondAss-perfValue">{performance.total}</span>
          </div>
          <div className="SecondAss-performanceItem SecondAss-currentItem">
            <span className="SecondAss-perfLabel">Current Obtained:</span>
            <span className="SecondAss-perfValue">{performance.obtained}</span>
          </div>
          <div className="SecondAss-performanceItem SecondAss-percentageItem">
            <span className="SecondAss-perfLabel">Overall Percentage:</span>
            <span className="SecondAss-perfValue SecondAss-percentage">
              {performance.percentage.toFixed(1)}%
            </span>
          </div>
          <div className="SecondAss-performanceItem SecondAss-gradeItem">
            <span className="SecondAss-perfLabel">Overall Grade:</span>
            <span 
              className="SecondAss-perfValue SecondAss-grade"
              style={{ backgroundColor: getGradeColor(performance.grade) }}
            >
              {performance.grade}
            </span>
          </div>
        </div>
      );
    }
  };

  // Initialize generation steps
  const initializeGenerationSteps = (studentCount, groupCount) => {
    const steps = [
      { id: 'prepare', label: 'Preparing document', icon: faFileAlt, completed: false },
      { id: 'data', label: 'Loading student data', icon: faUserGraduate, completed: false },
      { id: 'marks', label: 'Processing exam marks', icon: faClipboardList, completed: false },
      { id: 'calculate', label: 'Calculating grades', icon: faPercent, completed: false },
      { id: 'generate', label: 'Generating PDF pages', icon: faFilePdf, completed: false },
    ];
    
    if (groupCount > 1) {
      steps.push({ id: 'combine', label: 'Combining class PDFs', icon: faSchool, completed: false });
    }
    
    steps.push({ id: 'finalize', label: 'Finalizing document', icon: faCheckCircle, completed: false });
    
    setGenerationSteps(steps);
  };

  // Update generation step
  const updateGenerationStep = (stepId, completed = true) => {
    setGenerationSteps(prev => prev.map(step => 
      step.id === stepId ? { ...step, completed } : step
    ));
    setCurrentStep(stepId);
  };

  // Enhanced PDF generation with dynamic columns
  const generateStudentPageOnPDF = async (pdf, student, isFirstPage = false) => {
    if (!isFirstPage) {
      pdf.addPage();
    }

    const performance = calculateStudentPerformance(student.student_id);
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const hasPrevious = examConfig.hasPreviousAssessment;

    const leftMargin = 10;
    const topMargin = 10;
    let yPos = topMargin;

    // Fixed column coordinates
    const colX = {
      subject: leftMargin + 2,
      prevTotal: leftMargin + 55,
      prevObtained: leftMargin + 85,
      currTotal: leftMargin + 115,
      currObtained: leftMargin + 145,
      percentage: leftMargin + 172,
      grade: leftMargin + 192
    };

    // Simplified coordinates for 1st Assessment
    const simpleColX = {
        subject: leftMargin + 2,
        total: leftMargin + 75,
        obtained: leftMargin + 115,
        percentage: leftMargin + 155,
        grade: leftMargin + 190
    };

    const loadImage = (url) => {
      return new Promise((resolve) => {
        const img = new Image();
        img.src = url;
        img.crossOrigin = 'anonymous';
        img.onload = () => resolve(img);
        img.onerror = () => resolve(null);
      });
    };

    const logoLeft = await loadImage('/images/l1.jpeg');
    const logoRight = await loadImage('/images/l2.png');
    const stampImg = await loadImage('/images/stamp.png');

    // Decorative Background
    pdf.setGState(new pdf.GState({ opacity: 0.03 }));
    pdf.setFillColor(44, 50, 146);
    pdf.circle(0, 0, 65, 'F');
    pdf.circle(pageWidth * 0.8, 45, 12, 'F');
    pdf.setGState(new pdf.GState({ opacity: 1.0 }));

    // Header
    pdf.setFontSize(28);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(44, 50, 146);
    pdf.text('DAR-E-ARQAM', pageWidth / 2, yPos + 5, { align: 'center' });

    yPos += 12;
    pdf.setFontSize(12);
    pdf.setTextColor(100);
    pdf.text('KOTLI LOHARAN EAST CAMPUS', pageWidth / 2, yPos, { align: 'center' });

    yPos += 5;
    pdf.setFillColor(44, 50, 146);
    pdf.rect(leftMargin, yPos, pageWidth - 2 * leftMargin, 8, 'F');
    pdf.setTextColor(255);
    pdf.setFontSize(10);
    pdf.text('Project of Punjab Group of Colleges', pageWidth / 2, yPos + 5.5, { align: 'center' });

    yPos += 12;
    if (logoLeft) {
      const lW = 22;
      const lH = (logoLeft.height * lW) / logoLeft.width;
      pdf.addImage(logoLeft, 'JPEG', leftMargin + 5, yPos, lW, lH);
    }
    if (logoRight) {
      const rW = 22;
      const rH = (logoRight.height * rW) / logoRight.width;
      pdf.addImage(logoRight, 'PNG', pageWidth - leftMargin - rW - 5, yPos, rW, rH);
    }

    pdf.setFontSize(16);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(44, 50, 146);
    pdf.text(`${examType.toUpperCase()} RESULT`, pageWidth / 2, yPos + 10, { align: 'center' });
    pdf.setFontSize(10);
    pdf.text('Session 2024-2025', pageWidth / 2, yPos + 16, { align: 'center' });
    yPos += 35;

    // Student Details
    pdf.setDrawColor(225);
    pdf.line(leftMargin, yPos, pageWidth - leftMargin, yPos);
    yPos += 8;

    pdf.setFontSize(9);
    pdf.setTextColor(44, 50, 146);
    pdf.setFont('helvetica', 'bold');
    pdf.text('STUDENT NAME:', leftMargin + 2, yPos);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(0);
    pdf.text(student.name, leftMargin + 35, yPos);

    pdf.setTextColor(44, 50, 146);
    pdf.setFont('helvetica', 'bold');
    pdf.text('CLASS:', pageWidth / 2 + 10, yPos);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(0);
    pdf.text(student.class || 'N/A', pageWidth / 2 + 40, yPos);

    yPos += 6;
    pdf.setTextColor(44, 50, 146);
    pdf.setFont('helvetica', 'bold');
    pdf.text('SECTION:', leftMargin + 2, yPos);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(0);
    pdf.text(student.section || 'N/A', leftMargin + 35, yPos);

    pdf.setTextColor(44, 50, 146);
    pdf.setFont('helvetica', 'bold');
    pdf.text('STUDENT ID:', pageWidth / 2 + 10, yPos);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(0);
    pdf.text(student.student_id, pageWidth / 2 + 40, yPos);

    // --- SUBJECT PERFORMANCE TABLE ---
    yPos += 15;
    const headerBarHeight = 7;
    pdf.setFillColor(44, 50, 146);
    pdf.rect(leftMargin, yPos, pageWidth - 2 * leftMargin, headerBarHeight, 'F');
    pdf.setTextColor(255);
    pdf.setFontSize(11);
    pdf.text('Subjects Performance', leftMargin + 5, yPos + (headerBarHeight / 2) + 1.5);
    pdf.setTextColor(255, 166, 0);
    pdf.text('Detailed Marks Distribution', pageWidth - leftMargin - 5, yPos + (headerBarHeight / 2) + 1.5, { align: 'right' });

    yPos += headerBarHeight;
    const subHeaderHeight = hasPrevious ? 20 : 10;
    pdf.setFillColor(245, 247, 255);
    pdf.rect(leftMargin, yPos, pageWidth - 2 * leftMargin, subHeaderHeight, 'F');
    pdf.setTextColor(44, 50, 146);
    pdf.setFontSize(8);

    if (hasPrevious) {
      const prevMid = (colX.prevTotal + colX.prevObtained) / 2;
      const currMid = (colX.currTotal + colX.currObtained) / 2;

      pdf.text('Subjects', colX.subject, yPos + 8);
      pdf.text('Previous Assessment', prevMid, yPos + 5, { align: 'center' });
      pdf.text('(1st+Mid Term)', prevMid, yPos + 9, { align: 'center' });
      pdf.text('Current Assessment', currMid, yPos + 5, { align: 'center' });
      pdf.text('(2nd Assessment)', currMid, yPos + 9, { align: 'center' });

      pdf.text('Total', colX.prevTotal, yPos + 16, { align: 'center' });
      pdf.text('Obtained', colX.prevObtained, yPos + 16, { align: 'center' });
      pdf.text('Total', colX.currTotal, yPos + 16, { align: 'center' });
      pdf.text('Obtained', colX.currObtained, yPos + 16, { align: 'center' });
      pdf.text('Percentage', colX.percentage, yPos + 16, { align: 'center' });
      pdf.text('Grade', colX.grade, yPos + 16, { align: 'center' });
    } else {
      pdf.text('Subjects', simpleColX.subject, yPos + 6);
      pdf.text('Total Marks', simpleColX.total, yPos + 6, { align: 'center' });
      pdf.text('Obtained', simpleColX.obtained, yPos + 6, { align: 'center' });
      pdf.text('Percentage', simpleColX.percentage, yPos + 6, { align: 'center' });
      pdf.text('Grade', simpleColX.grade, yPos + 6, { align: 'center' });
    }

    yPos += subHeaderHeight;
    performance.subjects.forEach((subject, i) => {
      if (i % 2 === 0) {
        pdf.setFillColor(250, 250, 250);
        pdf.rect(leftMargin, yPos, pageWidth - 2 * leftMargin, 8, 'F');
      }
      pdf.setTextColor(0);
      
      if (hasPrevious) {
          pdf.text(subject.name, colX.subject, yPos + 5, { maxWidth: 45 });
          pdf.text(subject.previousTotal.toString(), colX.prevTotal, yPos + 5, { align: 'center' });
          pdf.text(subject.previousObtained.toFixed(2), colX.prevObtained, yPos + 5, { align: 'center' });
          pdf.text(subject.total.toString(), colX.currTotal, yPos + 5, { align: 'center' });
          pdf.text(subject.obtained.toString(), colX.currObtained, yPos + 5, { align: 'center' });
          pdf.text(`${subject.percentage.toFixed(1)}%`, colX.percentage, yPos + 5, { align: 'center' });
          
          const gColor = getGradeColor(subject.grade);
          pdf.setTextColor(parseInt(gColor.slice(1, 3), 16), parseInt(gColor.slice(3, 5), 16), parseInt(gColor.slice(5, 7), 16));
          pdf.text(subject.grade, colX.grade, yPos + 5, { align: 'center', fontStyle: 'bold' });
      } else {
          pdf.text(subject.name, simpleColX.subject, yPos + 5, { maxWidth: 65 });
          pdf.text(subject.total.toString(), simpleColX.total, yPos + 5, { align: 'center' });
          pdf.text(subject.obtained.toString(), simpleColX.obtained, yPos + 5, { align: 'center' });
          pdf.text(`${subject.percentage.toFixed(1)}%`, simpleColX.percentage, yPos + 5, { align: 'center' });
          
          const gColor = getGradeColor(subject.grade);
          pdf.setTextColor(parseInt(gColor.slice(1, 3), 16), parseInt(gColor.slice(3, 5), 16), parseInt(gColor.slice(5, 7), 16));
          pdf.text(subject.grade, simpleColX.grade, yPos + 5, { align: 'center', fontStyle: 'bold' });
      }
      yPos += 8;
    });

    // --- CONSOLIDATED OVERALL PERFORMANCE TABLE (LEFT ALIGNED) ---
    yPos += 10;
    pdf.setFillColor(44, 50, 146);
    pdf.rect(leftMargin, yPos, pageWidth - 2 * leftMargin, headerBarHeight, 'F');
    pdf.setTextColor(255);
    pdf.text('Overall Performance Summary', leftMargin + 5, yPos + (headerBarHeight / 2) + 1.5);

    yPos += headerBarHeight;
    const overallRowHeight = 15;
    pdf.setFillColor(245, 247, 255);
    pdf.rect(leftMargin, yPos, pageWidth - 2 * leftMargin, overallRowHeight, 'F');
    pdf.setTextColor(44, 50, 146);
    pdf.setFontSize(8);

    const summaryX = {
      total: leftMargin + 15,
      obtained: leftMargin + 65,
      percentage: leftMargin + 115,
      grade: leftMargin + 155
    };

    const grandTotal = (performance.previousTotal || 0) + (performance.total || 0);
    const grandObtained = (performance.previousObtained || 0) + (performance.obtained || 0);

    pdf.text(hasPrevious ? 'Grand Total Marks' : 'Total Marks', summaryX.total, yPos + 9);
    pdf.text(hasPrevious ? 'Grand Obtained Marks' : 'Obtained Marks', summaryX.obtained, yPos + 9);
    pdf.text('Total Percentage', summaryX.percentage, yPos + 9);
    pdf.text('Final Grade', summaryX.grade, yPos + 9);

    yPos += overallRowHeight;
    pdf.setTextColor(0);
    pdf.setFontSize(11);
    pdf.setFont('helvetica', 'bold');
    
    pdf.text(grandTotal.toString(), summaryX.total, yPos + 6);
    pdf.text(grandObtained.toFixed(2), summaryX.obtained, yPos + 6);
    pdf.text(`${performance.percentage.toFixed(1)}%`, summaryX.percentage, yPos + 6);
    
    const finalGradeColor = getGradeColor(performance.grade);
    pdf.setTextColor(parseInt(finalGradeColor.slice(1, 3), 16), parseInt(finalGradeColor.slice(3, 5), 16), parseInt(finalGradeColor.slice(5, 7), 16));
    pdf.text(performance.grade, summaryX.grade, yPos + 6);

    // --- FOOTER / GRADING SCHEME ---
    pdf.setFont('helvetica', 'normal');
    yPos = pageHeight - 75;
    const footerWidth = (pageWidth - leftMargin * 2 - 10) / 2;
    pdf.setFillColor(44, 50, 146);
    pdf.rect(leftMargin, yPos, footerWidth, 8, 'F');
    pdf.setTextColor(255);
    pdf.setFontSize(9);
    pdf.text('Grading Scheme', leftMargin + footerWidth / 2, yPos + 5.5, { align: 'center' });

    yPos += 8;
    const grades = [
      { g: 'A++', p: '90% or Above', r: 'Exceptional' },
      { g: 'A+', p: '80 to 89%', r: 'Excellent' },
      { g: 'A', p: '70 to 79%', r: 'Very Good' },
      { g: 'B+', p: '60 to 69%', r: 'Good' },
      { g: 'B', p: '50 to 59%', r: 'Fair' },
      { g: 'C', p: '40 to 49%', r: 'Need Improvement' },
      { g: 'F', p: 'Below 40%', r: 'Reappear' }
    ];

    pdf.setTextColor(0);
    pdf.setFontSize(7);
    grades.forEach((g, i) => {
      pdf.setDrawColor(230);
      pdf.rect(leftMargin, yPos + (i * 5), footerWidth, 5);
      pdf.text(g.g, leftMargin + 2, yPos + (i * 5) + 3.5);
      pdf.text(g.p, leftMargin + 12, yPos + (i * 5) + 3.5);
      pdf.text(g.r, leftMargin + footerWidth - 2, yPos + (i * 5) + 3.5, { align: 'right' });
    });

    const noteX = leftMargin + footerWidth + 10;
    pdf.setFillColor(255, 251, 230);
    pdf.rect(noteX, yPos - 8, footerWidth, 43, 'F');
    pdf.setTextColor(44, 50, 146);
    pdf.text('Important Note:', noteX + 5, yPos - 2);
    pdf.setTextColor(80);
    pdf.text("This report is system-generated. Discrepancies must be reported within 3 days.", noteX + 5, yPos + 5, { maxWidth: footerWidth - 10 });

    if (stampImg) {
      pdf.setGState(new pdf.GState({ opacity: 0.8 }));
      pdf.addImage(stampImg, 'PNG', noteX + footerWidth - 42, yPos + 10, 40, 25);
      pdf.setGState(new pdf.GState({ opacity: 1.0 }));
    }

    yPos = pageHeight - 20;
    const sigTitles = ['Principal', 'Class Teacher', 'Coordinator'];
    sigTitles.forEach((title, i) => {
      const x = leftMargin + (i * ((pageWidth - 20) / 3));
      pdf.line(x + 5, yPos, x + 55, yPos);
      pdf.text(title, x + 30, yPos + 5, { align: 'center' });
    });
  };

  const handleSelectStudent = (studentId) => {
    setSelectedStudents(prev => {
      if (prev.includes(studentId)) {
        return prev.filter(id => id !== studentId);
      } else {
        return [...prev, studentId];
      }
    });
  };

  const selectAllStudents = () => {
    if (selectedStudents.length === students.length) {
      setSelectedStudents([]);
    } else {
      setSelectedStudents(students.map(s => s.student_id));
    }
  };

  const toggleDetails = (studentId) => {
    setShowDetails(prev => ({
      ...prev,
      [studentId]: !prev[studentId]
    }));
  };

  const groupStudentsByClass = () => {
    const groups = {};
    students.forEach(student => {
      const className = student.class || 'Unknown Class';
      if (!groups[className]) {
        groups[className] = [];
      }
      groups[className].push(student);
    });
    return groups;
  };

  const handleGeneratePDFs = async () => {
    if (selectedStudents.length === 0) {
      Swal.fire({
        title: 'No Students Selected',
        text: 'Please select at least one student to generate result cards',
        icon: 'warning',
        confirmButtonColor: '#f59e0b',
      });
      return;
    }

    setShowGenerationModal(true);
    setPdfGenerating(true);
    setGenerationProgress(0);
    setGeneratedPDFs([]);
    
    const classGroups = groupByClass ? Object.keys(groupStudentsByClass()).length : 1;
    initializeGenerationSteps(selectedStudents.length, classGroups);
    
    try {
      updateGenerationStep('prepare');
      setGenerationProgress(5);
      await new Promise(resolve => setTimeout(resolve, 500));
      
      updateGenerationStep('data');
      setGenerationProgress(15);
      const selectedStudentData = students.filter(s => selectedStudents.includes(s.student_id));
      await new Promise(resolve => setTimeout(resolve, 300));
      
      updateGenerationStep('marks');
      setGenerationProgress(25);
      await new Promise(resolve => setTimeout(resolve, 400));
      
      updateGenerationStep('calculate');
      setGenerationProgress(35);
      await new Promise(resolve => setTimeout(resolve, 300));
      
      if (groupByClass && selectedStudentData.length > 1) {
        const classGroupsData = {};
        selectedStudentData.forEach(student => {
          const className = student.class || 'Unknown Class';
          if (!classGroupsData[className]) {
            classGroupsData[className] = [];
          }
          classGroupsData[className].push(student);
        });

        const classNames = Object.keys(classGroupsData);
        const generatedFiles = [];
        updateGenerationStep('generate');
        
        for (let i = 0; i < classNames.length; i++) {
          const className = classNames[i];
          const classStudents = classGroupsData[className];
          const classPDF = new jsPDF('p', 'mm', 'a4');
          
          for (let j = 0; j < classStudents.length; j++) {
            const student = classStudents[j];
            await generateStudentPageOnPDF(classPDF, student, j === 0);
            const progress = 35 + ((i * classStudents.length + j + 1) / (selectedStudentData.length)) * 50;
            setGenerationProgress(Math.min(progress, 85));
          }
          
          const fileName = `${examType.replace(/\s+/g, '_')}_${className.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
          classPDF.save(fileName);
          generatedFiles.push({
            className: className,
            fileName: fileName,
            studentCount: classStudents.length,
            timestamp: new Date().toLocaleTimeString()
          });
          setGenerationProgress(35 + ((i + 1) / classNames.length) * 50);
        }

        if (classNames.length > 1) {
          updateGenerationStep('combine');
          setGenerationProgress(90);
          await new Promise(resolve => setTimeout(resolve, 500));
        }
        setGeneratedPDFs(generatedFiles);
      } else {
        updateGenerationStep('generate');
        if (selectedStudentData.length === 1) {
          const student = selectedStudentData[0];
          const pdf = new jsPDF('p', 'mm', 'a4');
          await generateStudentPageOnPDF(pdf, student, true);
          const fileName = `${student.name.replace(/\s+/g, '_')}_${examType.replace(/\s+/g, '_')}_Result.pdf`;
          pdf.save(fileName);
          setGeneratedPDFs([{
            className: student.class || 'Single Student',
            fileName: fileName,
            studentCount: 1,
            timestamp: new Date().toLocaleTimeString()
          }]);
        } else {
          const multiPDF = new jsPDF('p', 'mm', 'a4');
          for (let i = 0; i < selectedStudentData.length; i++) {
            const student = selectedStudentData[i];
            await generateStudentPageOnPDF(multiPDF, student, i === 0);
            const progress = 35 + ((i + 1) / selectedStudentData.length) * 50;
            setGenerationProgress(Math.min(progress, 85));
          }
          const fileName = `${examType.replace(/\s+/g, '_')}_Multiple_Students_${new Date().toISOString().split('T')[0]}.pdf`;
          multiPDF.save(fileName);
          setGeneratedPDFs([{
            className: 'Multiple Students',
            fileName: fileName,
            studentCount: selectedStudentData.length,
            timestamp: new Date().toLocaleTimeString()
          }]);
        }
      }
      
      updateGenerationStep('finalize');
      setGenerationProgress(100);
      await new Promise(resolve => setTimeout(resolve, 500));
      
      setTimeout(() => {
        setShowGenerationModal(false);
        setPdfGenerating(false);
        Swal.fire({
          title: 'PDFs Generated Successfully!',
          icon: 'success',
          confirmButtonColor: '#10b981',
          confirmButtonText: 'View Generated Files'
        });
      }, 1000);
      
    } catch (error) {
      console.error('Error generating PDFs:', error);
      setShowGenerationModal(false);
      setPdfGenerating(false);
      Swal.fire({
        title: 'Error!',
        text: 'Failed to generate PDFs. Please try again.',
        icon: 'error',
        confirmButtonColor: '#d33',
      });
    }
  };

  const goBack = () => {
    navigate('/result-management');
  };

  const renderStudentCard = (student) => {
    const performance = calculateStudentPerformance(student.student_id);
    const isSelected = selectedStudents.includes(student.student_id);
    const isExpanded = showDetails[student.student_id];
    const hasMarks = examMarks[student.student_id] && examMarks[student.student_id].length > 0;

    return (
      <div key={student.student_id} className={`SecondAss-studentCard ${isSelected ? 'SecondAss-studentCardSelected' : ''}`}>
        <div className="SecondAss-cardHeader">
          <div className="SecondAss-cardCheckbox">
            <input
              type="checkbox"
              checked={isSelected}
              onChange={() => handleSelectStudent(student.student_id)}
            />
          </div>
          <div className="SecondAss-cardTitle">
            <h4>{student.name}</h4>
            <div className="SecondAss-cardSubtitle">
              <span className="SecondAss-studentId">{student.student_id}</span>
              <span className="SecondAss-classInfo">• {student.class} {student.section ? `- ${student.section}` : ''}</span>
              <span className="SecondAss-examType">• {examType}</span>
            </div>
          </div>
          <div className="SecondAss-cardActions">
            <button 
              className="SecondAss-actionBtn SecondAss-viewBtn"
              onClick={() => toggleDetails(student.student_id)}
              title={isExpanded ? "Hide Details" : "View Details"}
            >
              <FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown} />
            </button>
          </div>
        </div>
        
        <div className="SecondAss-cardBody">
          <div className="SecondAss-studentInfo">
            <div className="SecondAss-infoRow">
              <span className="SecondAss-infoLabel">Father Name:</span>
              <span className="SecondAss-infoValue">{student.father_name || 'N/A'}</span>
            </div>
            <div className="SecondAss-infoRow">
              <span className="SecondAss-infoLabel">Gender:</span>
              <span className="SecondAss-infoValue">{student.gender || 'N/A'}</span>
            </div>
            <div className="SecondAss-infoRow">
              <span className="SecondAss-infoLabel">Mobile:</span>
              <span className="SecondAss-infoValue">{student.mobile || 'N/A'}</span>
            </div>
          </div>
          
          {hasMarks && renderPerformanceSummary(performance)}
          
          {isExpanded && hasMarks && (
            <div className="SecondAss-detailedMarks">
              <div className="SecondAss-marksHeader">
                <h5>Subject-wise Marks</h5>
                <div className="SecondAss-examNote">
                  <FontAwesomeIcon icon={faInfoCircle} />
                  <span>Table columns dynamically adjusted for {examType}</span>
                </div>
              </div>
              <div className="SecondAss-marksTable">
                <table>
                  {renderTableHeaders()}
                  <tbody>
                    {renderSubjectRows(performance.subjects)}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          
          {!hasMarks && (
            <div className="SecondAss-noMarks">
              <FontAwesomeIcon icon={faTimes} />
              <span>No marks data available for {examType}</span>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderClassGroup = (className, classStudents) => {
    const selectedInClass = classStudents.filter(s => selectedStudents.includes(s.student_id)).length;
    const allSelected = selectedInClass === classStudents.length && classStudents.length > 0;

    return (
      <div key={className} className="SecondAss-classGroup">
        <div className="SecondAss-classHeader">
          <div className="SecondAss-classTitle">
            <FontAwesomeIcon icon={faSchool} />
            <h3>{className}</h3>
            <span className="SecondAss-classCount">{classStudents.length} students</span>
          </div>
          <div className="SecondAss-classActions">
            <button 
              className="SecondAss-classSelectBtn"
              onClick={() => {
                const classStudentIds = classStudents.map(s => s.student_id);
                if (allSelected) {
                  setSelectedStudents(prev => prev.filter(id => !classStudentIds.includes(id)));
                } else {
                  setSelectedStudents(prev => [...new Set([...prev, ...classStudentIds])]);
                }
              }}
            >
              {allSelected ? 'Deselect All' : 'Select All'} ({selectedInClass}/{classStudents.length})
            </button>
          </div>
        </div>
        
        <div className="SecondAss-classStudents">
          {viewMode === 'grid' ? (
            <div className="SecondAss-studentsGrid">
              {classStudents.map(student => renderStudentCard(student))}
            </div>
          ) : (
            <div className="SecondAss-studentsList">
              {classStudents.map(student => (
                <div key={student.student_id} className="SecondAss-studentListItem">
                  <div className="SecondAss-listCheckbox">
                    <input
                      type="checkbox"
                      checked={selectedStudents.includes(student.student_id)}
                      onChange={() => handleSelectStudent(student.student_id)}
                    />
                  </div>
                  <div className="SecondAss-listInfo">
                    <span className="SecondAss-listName">{student.name}</span>
                    <span className="SecondAss-listDetails">
                      {student.student_id} • {student.class} • {student.section || 'N/A'} • {examType}
                    </span>
                  </div>
                  <div className="SecondAss-listActions">
                    <button 
                      className="SecondAss-actionBtn"
                      onClick={() => toggleDetails(student.student_id)}
                    >
                      <FontAwesomeIcon icon={showDetails[student.student_id] ? faChevronUp : faChevronDown} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderGenerationModal = () => {
    if (!showGenerationModal) return null;

    return (
      <div className="SecondAss-modal-overlay">
        <div className="SecondAss-modal SecondAss-generation-modal">
          <div className="SecondAss-modal-header">
            <h3>
              <FontAwesomeIcon icon={faFilePdf} />
              Generating Result Cards
            </h3>
            <div className="SecondAss-generation-stats">
              <span className="SecondAss-generation-progress">
                {generationProgress.toFixed(0)}%
              </span>
            </div>
          </div>
          
          <div className="SecondAss-modal-content">
            <div className="SecondAss-spinner-wrapper">
              <div className="SecondAss-spinner"></div>
              <div className="SecondAss-spinner-inner"></div>
            </div>
            
            <div className="SecondAss-generation-progress-section">
              <div className="SecondAss-progress-bar">
                <div 
                  className="SecondAss-progress-fill"
                  style={{ width: `${generationProgress}%` }}
                ></div>
              </div>
              <div className="SecondAss-progress-info">
                <span className="SecondAss-progress-text">
                  {currentStep ? `Processing: ${currentStep}` : 'Starting generation...'}
                </span>
                <span className="SecondAss-progress-percentage">
                  {generationProgress.toFixed(0)}%
                </span>
              </div>
            </div>
            
            <div className="SecondAss-generation-steps">
              {generationSteps.map((step, index) => (
                <div 
                  key={step.id} 
                  className={`SecondAss-step-item ${step.completed ? 'SecondAss-step-completed' : ''} ${currentStep === step.id ? 'SecondAss-step-active' : ''}`}
                >
                  <div className="SecondAss-step-icon">
                    {step.completed ? (
                      <FontAwesomeIcon icon={faCheckCircle} className="SecondAss-step-check" />
                    ) : (
                      <FontAwesomeIcon icon={step.icon} />
                    )}
                  </div>
                  <div className="SecondAss-step-content">
                    <div className="SecondAss-step-label">{step.label}</div>
                    <div className="SecondAss-step-status">
                      {step.completed ? 'Completed' : currentStep === step.id ? 'In Progress...' : 'Pending'}
                    </div>
                  </div>
                  {step.completed && (
                    <div className="SecondAss-step-indicator">
                      <div className="SecondAss-step-dot"></div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
          
          <div className="SecondAss-modal-footer">
            <button 
              className="SecondAss-btn SecondAss-btn-secondary"
              onClick={() => {
                setShowGenerationModal(false);
                setPdfGenerating(false);
              }}
              disabled={generationProgress >= 95}
            >
              <FontAwesomeIcon icon={faTimes} />
              {generationProgress >= 95 ? 'Almost Complete' : 'Cancel'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="dashboard-wrapper">
        <Sidebar />
        <div className="ck-main-content" style={{ marginLeft: "18rem", width: "calc(100% - 18rem)" }}>
          <div className="SecondAss-loading">
            <div className="SecondAss-spinner-ring">
              <div></div><div></div><div></div>
            </div>
            <p className="SecondAss-loadingTitle">Loading Exam Data...</p>
          </div>
        </div>
      </div>
    );
  }

  const classGroups = groupStudentsByClass();
  const totalSelected = selectedStudents.length;
  const hasMarksData = Object.keys(examMarks).length > 0;

  return (
    <div className="dashboard-wrapper">
      <Sidebar />
      <div className="main-content" style={{ marginLeft: "18rem", width: "calc(100% - 18rem)" }}>
        <section className="ck-content">
          <div className="SecondAss-header">
            <div className="SecondAss-headerContent">
              
              <div className="SecondAss-titleSection">
                <h1 className="SecondAss-title">
                  <FontAwesomeIcon icon={faFilePdf} className="SecondAss-headerIcon" />
                  Result Card Generator
                </h1>
                <div className="SecondAss-examTypeBadge">
                  <FontAwesomeIcon icon={faClipboardList} />
                  <span>{examConfig.displayName}</span>
                </div>
              </div>
            </div>
            
            <div className="SecondAss-headerActions">
              <button 
                className="SecondAss-btn SecondAss-btnSecondary"
                onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
              >
                <FontAwesomeIcon icon={viewMode === 'grid' ? faList : faBars} />
                {viewMode === 'grid' ? 'List View' : 'Grid View'}
              </button>
              
              <button 
                className="SecondAss-btn SecondAss-btnPrimary"
                onClick={handleGeneratePDFs}
                disabled={pdfGenerating || totalSelected === 0}
              >
                <FontAwesomeIcon icon={faFilePdf} />
                Generate PDFs ({totalSelected})
              </button>
            </div>
          </div>

          <div className="SecondAss-optionsSection">
            <div className="SecondAss-optionsGrid">
              <div className="SecondAss-optionCard">
                <div className="SecondAss-optionIcon"><FontAwesomeIcon icon={faUsers} /></div>
                <div className="SecondAss-optionContent">
                  <h4>Grouping Options</h4>
                  <label className="SecondAss-optionToggle">
                    <input
                      type="checkbox"
                      checked={groupByClass}
                      onChange={(e) => setGroupByClass(e.target.checked)}
                      disabled={totalSelected <= 1}
                    />
                    <span className="SecondAss-toggleSlider"></span>
                    <span className="SecondAss-toggleLabel">Generate separate PDF for each class</span>
                  </label>
                </div>
              </div>
              
              <div className="SecondAss-optionCard">
                <div className="SecondAss-optionIcon"><FontAwesomeIcon icon={faCog} /></div>
                <div className="SecondAss-optionContent">
                  <h4>Quick Actions</h4>
                  <button className="SecondAss-quickBtn" onClick={selectAllStudents}>
                    {selectedStudents.length === students.length ? 'Deselect All' : 'Select All'}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="SecondAss-studentsSection" >
            <div className="SecondAss-classesContainer" >
              {Object.entries(classGroups).map(([className, classStudents]) => 
                renderClassGroup(className, classStudents)
              )}
            </div>
          </div>

          <div className="SecondAss-footerActions">
            <div className="SecondAss-actionButtons">
              <button 
                className="SecondAss-btn SecondAss-btnSecondary"
                onClick={() => setSelectedStudents([])}
              >
                <FontAwesomeIcon icon={faTimes} />
                Clear Selection
              </button>
              
              <button 
                className="SecondAss-btn SecondAss-btnPrimary"
                onClick={handleGeneratePDFs}
                disabled={pdfGenerating || totalSelected === 0}
              >
                <FontAwesomeIcon icon={faFilePdf} />
                Generate PDF
              </button>
            </div>
          </div>
        </section>
      </div>
      {renderGenerationModal()}
    </div>
  );
};

export default ResultCardGenerator;